import PropTypes from "prop-types";
import { createContext, useEffect, useReducer, useCallback } from "react";
// utils
import axios from "../utils/axios";

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user:
        action.payload.user !== undefined ? action.payload.user : state.user,
    };
  }
  if (action.type === "LOGIN") {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      // user: null,
    };
  }
  if (action.type === "GET_AUTH_CODE") {
    const { payload } = action;
    return { ...state, user: { ...state.user, ...payload } };
  }
  if (
    action.type === "SET_LANGUAGE_SUCCESS" ||
    action.type === "CANCEL_SUCCESS"
  ) {
    const { payload } = action;
    return { ...state, ...payload };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // INITIALIZE
  const initialize = useCallback(async () => {
    try {
      const response = await axios.get("/user/profile");
      const user = { ...response.data };

      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: true,
          user,
        },
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: false,
          // user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = async (email, password) => {
    const response = await axios.post("/auth/login", { email, password });

    const { user } = response.data;

    dispatch({
      type: "LOGIN",
      payload: { user },
    });
  };

  // REGISTER
  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post("/api/account/register", {
      email,
      password,
      firstName,
      lastName,
    });

    const { user } = response.data;

    dispatch({
      type: "REGISTER",
      payload: { user },
    });
  };

  // LOGOUT
  const logout = useCallback(async () => {
    try {
      await axios.post("/auth/logout-cookies");
    } catch (error) {
      console.error("LOGOUT ERROR", error);
    }

    dispatch({ type: "LOGOUT" });
  }, []);

  // RESET PASSWORD CONFIRM
  const resetPasswordConfirm = async ({ password, email, token }) => {
    const response = await axios.post("/user/reset-password-confirm", {
      password,
      email,
      token,
    });

    const { user } = response.data;

    dispatch({
      type: "LOGIN",
      payload: { user },
    });
  };

  // CONFIRM EMAIL
  const confirmEmail = async (token) => {
    const response = await axios.post("/user/email-confirm", { token });

    if (response && response.data?.user) {
      const { user } = response.data;

      dispatch({
        type: "LOGIN",
        payload: { user },
      });

      return "success";
    } else {
      return "alreadyVerified";
    }
  };

  // CONFIRM EMAIL OLD
  const confirmEmailOld = async (userID, token) => {
    const response = await axios.post("/user/email-confirm-outdated", {
      userID,
      token,
    });
    const { user } = response.data;

    dispatch({
      type: "LOGIN",
      payload: { user },
    });
  };

  const resetPassword = async (email) => {
    await axios.post("/user/reset-password", { email });
  };

  const changePassword = async (newPassword, oldPassword) => {
    await axios.post("/user/change-password", { oldPassword, newPassword });
  };

  const getVpnToken = async (id, token) => {
    const response = await axios.post("/user/setup-vpn", {
      id: Number(id),
      setupVpnToken: token,
    });
    const { vpnAuthCode, status } = response.data;
    if (vpnAuthCode) {
      dispatch({ type: "GET_AUTH_CODE", payload: { vpnAuthCode, status } });
    }
  };

  const getVpnTokenOld = async (id, token) => {
    const response = await axios.post("/user/setup-vpn-outdated", {
      id: Number(id),
      setupVpnToken: token,
    });
    const { vpnAuthCode, status } = response.data;
    if (vpnAuthCode) {
      dispatch({ type: "GET_AUTH_CODE", payload: { vpnAuthCode, status } });
    }
  };

  const promoUpgrade = async (id, token) => {
    const response = await axios.post("/payment/upgraded-2gb-users", {
      id: Number(id),
      hash: token.toString(),
    });
    return response.data;
  };

  const setLanguage = async (language) => {
    try {
      const response = await axios.post("/user/change-language", { language });
      dispatch({
        type: "SET_LANGUAGE_SUCCESS",
        payload: { user: response.data },
      });
    } catch (e) {
      const { user } = state;
      dispatch({
        type: "SET_LANGUAGE_SUCCESS",
        payload: { user: { ...user, language } },
      });
    }
  };

  const cancelSubscription = async (email) => {
    const response = await axios.post("/payment/cancel-subscription", {
      email,
    });
    dispatch({
      type: "CANCEL_SUCCESS",
      payload: { user: response.data },
    });
  };

  useEffect(() => {
    const handleTokenExpired = () => {
      logout();
    };

    window.addEventListener("onTokenExpired", handleTokenExpired);

    return () => {
      window.removeEventListener("onTokenExpired", handleTokenExpired);
    };
  }, [logout]);
  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        login,
        logout,
        register,
        resetPassword,
        resetPasswordConfirm,
        changePassword,
        confirmEmail,
        getVpnToken,
        setLanguage,
        cancelSubscription,
        confirmEmailOld,
        getVpnTokenOld,
        promoUpgrade,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
